import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Spin } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import RouteCard from "./RouteCard";
import DocStatusPill from "./DocStatusPill";
import DocLineTable from "./DocLineTable";
import ShipmentCard from "./ShipmentCard";
import HistoryTimeline from "./HistoryTimeline";
import ShipModal from "./ShipModal";
import BuyModal from "./BuyModal";
import ReturnModal from "./ReturnModal";
import DiscrepancyModal from "./DiscrepancyModal";

// Matches Diamond-PO-Prototype-v3.html's docDetail() (lines 943-999). Every action button in the
// prototype now opens/calls the real thing: Ship (2180/F3), Accept (2183/F4), Buy selected
// (2182/F4), Ship return / Report a discrepancy / Clarified-keep / Take it back (2186/2188/F5).

const toRouteAddress = (snap) =>
  snap && {
    Label: snap.label,
    ContactName: snap.contact,
    Address: snap.line1,
    City: snap.city,
    State: snap.state,
    Zip: snap.zip,
    Country: snap.country,
    Phone: snap.phone,
  };

export const DocumentDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [buyOpen, setBuyOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [discLineId, setDiscLineId] = useState(null);

  const load = useCallback(() => {
    if (!dealerId || !id) return;
    setLoading(true);
    PurchaseOrderService.GetDiamondDoc(id, { DealerID: String(dealerId) })
      .then((response) => {
        setLoading(false);
        if (response.data.status === true) {
          setDetail(response.data.responseData);
        } else {
          NotificationManager.error(t(response.data.message || "Could not load this document"));
        }
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId, id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !detail) {
    return (
      <div style={{ padding: 20 }}>
        <Spin spinning={loading} />
      </div>
    );
  }

  const { Doc: doc, Lines: lines, Shipments: shipments, Events: events, Charges: charges } = detail;
  const isRetailer = String(dealerId) === String(doc.RetailerDealerID);
  const isMemo = doc.DocKind === "MEMO";
  const backPath = isRetailer ? (isMemo ? "/my-memos" : "/purchase-orders") : "/requests";

  const requestedCount = lines.filter((l) => l.StatusCode === "REQUESTED").length;
  const anyOut = lines.some((l) => l.StatusCode === "OUT");
  // Matches the prototype's own acceptPO() button gate (docDetail(), line 973): PO only, at
  // least one SHIPPED line, every OUT shipment on the doc already delivered.
  const canAccept =
    isRetailer && !isMemo && lines.some((l) => l.StatusCode === "SHIPPED") && shipments.every((s) => s.Direction !== "OUT" || s.DeliveredUtc);
  // Matches lineRow()'s own selectable() exactly (docDetail(), line 950): memo -- OUT or
  // RETURN_REQUESTED; PO -- RETURN_REQUESTED only (title has passed, a PO can only go back via
  // the discrepancy flow).
  const isLineSelectable = (l) => isRetailer && (isMemo ? ["OUT", "RETURN_REQUESTED"].includes(l.StatusCode) : l.StatusCode === "RETURN_REQUESTED");
  const selectedLines = lines.filter((l) => selectedLineIds.includes(l.DiamondLineID));
  const canBuySelected = isMemo && selectedLines.some((l) => l.StatusCode === "OUT");
  const showStoneActions = isRetailer && (isMemo || selectedLineIds.length > 0);
  const shipToJson = typeof doc.ShipToJson === "string" ? JSON.parse(doc.ShipToJson) : doc.ShipToJson;
  const outShipment = shipments.find((s) => s.Direction === "OUT");
  const shipFromJson = outShipment
    ? outShipment.FromJson
    : doc.ShipFromJson
    ? typeof doc.ShipFromJson === "string"
      ? JSON.parse(doc.ShipFromJson)
      : doc.ShipFromJson
    : null;
  const shipmentsById = Object.fromEntries(shipments.map((s) => [s.ShipmentID, s]));

  const runAction = (promise, successMessage) => {
    setActing(true);
    promise
      .then((response) => {
        setActing(false);
        if (response.data.status === true) {
          NotificationManager.success(t(successMessage));
          load();
        } else {
          NotificationManager.error(t(response.data.message || "Could not do that"));
        }
      })
      .catch(() => setActing(false));
  };

  const handleCancelUnsent = () =>
    runAction(PurchaseOrderService.CancelUnsentDiamondDoc(id, { DealerID: String(dealerId) }), "Unsent stones cancelled");
  const handleExtend = () => runAction(PurchaseOrderService.ExtendDiamondDoc(id, { DealerID: String(dealerId) }), "Extended by 7 days");
  const handleConfirmReceipt = (shipmentId) =>
    runAction(PurchaseOrderService.ConfirmDiamondShipmentReceipt(shipmentId, { DealerID: String(dealerId) }), "Receipt confirmed");
  const handleAccept = () =>
    runAction(PurchaseOrderService.AcceptDiamondDoc(id, { DealerID: String(dealerId) }), "Accepted — this purchase order is now the invoice");
  const handleReceiveReturn = (shipmentId) =>
    runAction(PurchaseOrderService.ReceiveDiamondShipment(shipmentId, { DealerID: String(dealerId) }), "Received — relisted");
  const handleResolveDiscrepancy = (lineId, resolution) =>
    runAction(
      PurchaseOrderService.ResolveDiamondDiscrepancy(lineId, { DealerID: String(dealerId), Resolution: resolution }),
      resolution === "CLARIFY" ? "Resolved — the line is open again" : "Return requested — the retailer ships it back with tracking"
    );

  return (
    <div style={{ padding: 20, paddingBottom: 60 }}>
      <Spin spinning={acting}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
              {doc.DocNumber} <DocStatusPill status={doc.StatusCode} />
            </h2>
            <p style={{ color: "#888", margin: 0 }}>
              {isMemo ? t("Memo") : t("Purchase order")} · {doc.SupplierName} → {doc.RetailerName}
              {doc.ReferenceNo ? ` · ${t("Ref")} ${doc.ReferenceNo}` : ""}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button onClick={() => navigate(backPath)}>{t("Back")}</Button>
            {!isRetailer && requestedCount > 0 && (
              <Button type="primary" onClick={() => setShipOpen(true)}>
                {isMemo ? t("Ship memo") : t("Confirm & ship")}
              </Button>
            )}
            {!isRetailer && isMemo && anyOut && (
              <Button onClick={handleExtend}>{t("Extend 7 days")}</Button>
            )}
            {canAccept && (
              <Button type="primary" onClick={handleAccept}>
                {t("Accept stones")}
              </Button>
            )}
            {isRetailer && requestedCount > 0 && (
              <Button danger onClick={handleCancelUnsent}>
                {t("Cancel unsent stones")}
              </Button>
            )}
          </div>
        </div>

        {doc.StatusCode === "OVERDUE" && (
          <div style={{ background: "#fdecea", border: "1px solid #f6d5d1", borderRadius: 6, padding: 14, marginBottom: 16 }}>
            <b>{t("Past due")}</b>{" "}
            {isRetailer ? t("Buy these stones or send them back.") : t("Chase the retailer, extend, or invoice.")}
          </div>
        )}
        {doc.StatusCode === "PARTSHIP" && (
          <div style={{ background: "#e6f4ff", border: "1px solid #91caff", borderRadius: 6, padding: 14, marginBottom: 16 }}>
            <b>{t("Part shipped")}</b> {requestedCount} {t("stone(s) still to come.")}{" "}
            {isRetailer
              ? t("The supplier is holding them for a later shipment.")
              : t("They stay held for this retailer until you ship or decline them.")}
          </div>
        )}

        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>{t("Addresses on this {{doc}}", { doc: isMemo ? t("memo") : t("order") })}</h3>
          <RouteCard
            fromAddress={toRouteAddress(shipFromJson)}
            toAddress={toRouteAddress(shipToJson)}
            fromPartyName={doc.SupplierName}
            toPartyName={doc.RetailerName}
          />
        </div>

        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0 }}>{t("Stones")}</h3>
            {showStoneActions && (
              <div style={{ display: "flex", gap: 8 }}>
                {isMemo && (
                  <Button size="small" type="primary" disabled={!canBuySelected} onClick={() => setBuyOpen(true)}>
                    {t("Buy selected")}
                  </Button>
                )}
                <Button size="small" disabled={selectedLineIds.length === 0} onClick={() => setReturnOpen(true)}>
                  {t("Ship return")}
                  {selectedLineIds.length > 0 ? ` (${selectedLineIds.length})` : ""}
                </Button>
              </div>
            )}
          </div>
          <DocLineTable
            lines={lines}
            docKind={doc.DocKind}
            isRetailer={isRetailer}
            shipmentsById={shipmentsById}
            selectable={isRetailer ? isLineSelectable : undefined}
            selectedIds={selectedLineIds}
            onToggleSelect={(lineId, checked) =>
              setSelectedLineIds((prev) => (checked ? [...prev, lineId] : prev.filter((id) => id !== lineId)))
            }
            onReportDiscrepancy={isRetailer ? (lineId) => setDiscLineId(lineId) : undefined}
            onResolveDiscrepancy={!isRetailer ? handleResolveDiscrepancy : undefined}
          />
          {!isMemo && (
            <div style={{ maxWidth: 360, marginLeft: "auto", marginTop: 12, padding: "12px 0", borderTop: "1px solid #eee" }}>
              {charges.map((c) => (
                <div key={c.ChargeID} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span>{c.Label}</span>
                  <span>${Number(c.Amount).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {shipments.map((s) => (
          <ShipmentCard
            key={s.ShipmentID}
            shipment={s}
            docNumber={doc.DocNumber}
            docKind={doc.DocKind}
            isRetailer={isRetailer}
            lineCount={lines.filter((l) => l.OutShipmentID === s.ShipmentID || l.ReturnShipmentID === s.ShipmentID).length}
            onConfirmReceipt={handleConfirmReceipt}
            onReceive={handleReceiveReturn}
          />
        ))}

        <HistoryTimeline
          events={events}
          retailerDealerId={doc.RetailerDealerID}
          retailerName={doc.RetailerName}
          supplierDealerId={doc.SupplierDealerID}
          supplierName={doc.SupplierName}
        />
      </Spin>
      {shipOpen && <ShipModal docId={id} onClose={() => setShipOpen(false)} onDone={load} />}
      {buyOpen && (
        <BuyModal
          memoId={id}
          lineIds={selectedLineIds.filter((lid) => lines.find((l) => l.DiamondLineID === lid)?.StatusCode === "OUT")}
          onClose={() => setBuyOpen(false)}
          onDone={() => {
            setSelectedLineIds([]);
            load();
          }}
        />
      )}
      {returnOpen && (
        <ReturnModal
          docId={id}
          lineIds={selectedLineIds}
          onClose={() => setReturnOpen(false)}
          onDone={() => {
            setSelectedLineIds([]);
            load();
          }}
        />
      )}
      {discLineId && (
        <DiscrepancyModal
          lineId={discLineId}
          snap={(() => {
            const l = lines.find((x) => x.DiamondLineID === discLineId);
            if (!l) return null;
            return typeof l.StoneSnapshotJson === "string" ? JSON.parse(l.StoneSnapshotJson) : l.StoneSnapshotJson;
          })()}
          supplierName={doc.SupplierName}
          onClose={() => setDiscLineId(null)}
          onDone={load}
        />
      )}
    </div>
  );
};

export default DocumentDetail;
